import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Plus, Trash2, Send, Disc3, Lock, Music, Video, Eye, Play, Sparkles } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, EmptyState } from "@/components/dashboard/ui";
import ReleaseEditorDialog from "./ReleaseEditorDialog";
import ReleaseDetailDialog from "@/components/releases/ReleaseDetailDialog";
import DolbyAtmosDialog from "./DolbyAtmosDialog";

export default function ArtistReleases() {
  const { user } = useAuth();
  const distActive = !!user?.distribution_active;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [initialType, setInitialType] = useState("Single");
  const [detailId, setDetailId] = useState(null);
  const [dolbyRelease, setDolbyRelease] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/artist/releases");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load releases");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-resume submit after Dolby Atmos payment lands via ?dolby_paid=<id>
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const paidId = params.get("dolby_paid");
    if (!paidId) return;
    (async () => {
      try {
        await api.post(`/artist/releases/${paidId}/dolby-sync`);
        toast.success("Dolby Atmos payment confirmed — submitting your release for review");
        try {
          await api.post(`/artist/releases/${paidId}/submit`);
          toast.success("Submitted for review");
        } catch (err) {
          toast.error(formatApiError(err, "Payment confirmed, but submit failed — try again."));
        }
        load();
      } catch (err) {
        toast.error(formatApiError(err, "We couldn't verify the Dolby payment yet — refresh in a few seconds."));
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete("dolby_paid");
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.toString());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitForReview = async (id) => {
    try {
      await api.post(`/artist/releases/${id}/submit`);
      toast.success("Submitted for review");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Dolby Atmos payment required — open the paid add-on dialog
      if (err?.response?.status === 402 && detail && typeof detail === "object" && detail.code === "dolby_atmos_payment_required") {
        const rel = rows.find((r) => r.release_id === id) || { release_id: id };
        setDolbyRelease(rel);
        return;
      }
      toast.error(formatApiError(err, "Failed to submit"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/artist/releases/${id}`);
      toast.success("Release deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openNew = (type) => {
    setInitialType(type);
    setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="artist-releases">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">My Releases</h2>
          <p className="mt-1.5 text-slate-400">Create a draft, then submit it for review to go live.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="new-music-video-btn"
            disabled={!distActive}
            onClick={() => openNew("Music Video")}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 text-white font-medium px-5 py-3 hover:bg-white/[0.1] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Video size={18} /> New music video
          </button>
          <button
            data-testid="new-release-btn"
            disabled={!distActive}
            onClick={() => openNew("Single")}
            className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-3 hover:bg-slate-200 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={18} /> New release
          </button>
        </div>
      </div>

      <ReleaseEditorDialog
        open={open}
        onOpenChange={setOpen}
        onSaved={load}
        initialType={initialType}
      />

      {!distActive && (
        <div data-testid="dist-locked-banner" className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] to-transparent px-5 py-4 flex items-start gap-3">
          <Lock size={18} className="text-amber-300 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm text-amber-100">
              Subscribe to unlock distribution
            </div>
            <div className="mt-0.5 text-xs text-amber-200/80">
              Pick an Artist, Pro or Label plan from the <span className="text-white">Billing</span> tab before you create your first release or music video.
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="releases-skeleton">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="aspect-square bg-gradient-to-br from-blue-900/20 to-transparent nz-shimmer" />
              <div className="p-5 space-y-3">
                <div className="h-4 w-2/3 rounded-md bg-white/5 nz-shimmer" />
                <div className="h-3 w-1/2 rounded-md bg-white/5 nz-shimmer" />
                <div className="h-9 w-full rounded-lg bg-white/[0.03] nz-shimmer mt-2" />
              </div>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="No releases yet" desc="Hit 'New release' to create your first drop." />
      ) : (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
        >
          <AnimatePresence>
            {rows.map((r) => {
              const isVideo = r.type === "Music Video";
              return (
                <motion.div
                  key={r.release_id}
                  data-testid={`release-card-${r.release_id}`}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 220, damping: 26 } },
                  }}
                  exit={{ opacity: 0, y: -12, transition: { duration: 0.18 } }}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden group hover:border-white/20 hover:shadow-[0_20px_60px_-20px_rgba(56,127,255,0.35)] transition-[border-color,box-shadow] duration-300"
                >
                  <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-blue-900/40 to-[#040a1a] flex items-center justify-center">
                  {r.cover_url ? (
                    <img
                      src={mediaUrl(r.cover_url)}
                      alt={r.title}
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling?.classList.remove("hidden"); }}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                    />
                  ) : null}
                  <Disc3 size={44} className={`text-blue-400/50 transition-transform duration-500 group-hover:rotate-90 group-hover:scale-110 ${r.cover_url ? "hidden" : ""}`} />
                    {isVideo && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    )}
                    <div className="absolute top-3 right-3"><StatusPill status={r.status} /></div>
                    <div className="absolute top-3 left-3 flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-white/80 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-2 py-1">
                      {isVideo ? <><Video size={10} /> Video</> : <><Music size={10} /> {r.type}</>}
                    </div>
                    {r.tracks?.[0]?.audio_url && (
                      <button
                        type="button"
                        onClick={() => setDetailId(r.release_id)}
                        aria-label="Preview"
                        className="absolute bottom-3 right-3 h-11 w-11 rounded-full bg-white text-black flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-lg"
                      >
                        <Play size={16} className="ml-0.5" />
                      </button>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-white font-medium truncate">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {r.type}{r.genre ? ` · ${r.genre}` : ""}{r.upc ? ` · UPC ${r.upc}` : ""}
                    </div>
                    {r.tracks?.length > 0 && (
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                        <Music size={13} />
                        <span>{r.tracks.length} track{r.tracks.length > 1 ? "s" : ""}</span>
                      </div>
                    )}
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        data-testid={`release-view-${r.release_id}`}
                        onClick={() => setDetailId(r.release_id)}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 hover:bg-white/5 text-white text-sm py-2 transition-colors"
                      >
                        <Eye size={14} /> View
                      </button>
                      {r.status === "Draft" && (
                        <button
                          data-testid={`release-submit-${r.release_id}`}
                          onClick={() => submitForReview(r.release_id)}
                          className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white text-black text-sm py-2 hover:bg-slate-200 transition-colors font-medium"
                        >
                          <Send size={14} /> Submit
                        </button>
                      )}
                      <button
                        data-testid={`release-delete-${r.release_id}`}
                        onClick={() => remove(r.release_id)}
                        className="inline-flex items-center justify-center rounded-lg border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 text-slate-400 hover:text-red-400 p-2 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      <ReleaseDetailDialog
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        releaseId={detailId}
        isOwner
        onChanged={load}
      />

      <DolbyAtmosDialog
        release={dolbyRelease}
        onClose={() => setDolbyRelease(null)}
        onPaid={async (id) => {
          try {
            await api.post(`/artist/releases/${id}/submit`);
            toast.success("Submitted for review");
            load();
          } catch (err) {
            toast.error(formatApiError(err, "Failed to submit"));
          }
        }}
      />
    </div>
  );
}
